import React, { FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useNavbar } from '../../../hooks/use-navbar';
import { useClient } from '../../../hooks/use-client';

const Icon: FC<{ name: string; size?: number }> = ({ name, size = 28 }) => {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (name === 'camera') {
    return (
      <svg {...props}>
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
      </svg>
    );
  }
  if (name === 'edit') {
    return (
      <svg {...props}>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
      </svg>
    );
  }
  if (name === 'arrow-right') {
    return (
      <svg {...props} strokeWidth={2.2}>
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    );
  }
  return null;
};

const ClientNewChoice: FC = () => {
  const navbar = useNavbar();
  const navigate = useNavigate();
  const { clientId } = useParams<'clientId'>();
  const { client } = useClient(clientId ? +clientId : 0);

  useEffect(() => {
    if (!navbar) {
      return;
    }
    navbar.setTitle('Nouvelle intervention');
    navbar.setBackArrow(true);
    navbar.setOnBack(() => navigate(`/client/${clientId}`));
    navbar.setRight();
  }, [navbar, clientId, navigate]);

  return (
    <div style={{ padding: '14px 14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {client && (
        <div
          style={{
            background: 'rgba(49,220,200,0.08)',
            border: '1px solid rgba(49,220,200,0.3)',
            borderRadius: 10,
            padding: '10px 12px',
            color: 'var(--text-primary)',
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--teal)',
              marginBottom: 2,
            }}
          >
            Client
          </div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{client.name || client.display_name}</div>
        </div>
      )}

      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: 'var(--text-primary)',
          margin: '4px 0 4px',
          letterSpacing: '-0.01em',
        }}
      >
        Comment voulez-vous commencer ?
      </div>

      <button
        type="button"
        onClick={() => navigate(`/photo?from_client=${clientId}`)}
        style={{
          background: 'rgba(49,220,200,0.07)',
          border: '1px solid rgba(49,220,200,0.45)',
          borderRadius: 16,
          padding: '18px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          cursor: 'pointer',
          textAlign: 'left',
          color: 'var(--text-primary)',
          fontFamily: 'inherit',
          width: '100%',
        }}
      >
        <span
          style={{
            width: 50,
            height: 50,
            borderRadius: 13,
            background: 'rgba(49,220,200,0.14)',
            color: 'var(--teal)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="camera" size={28} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--teal)',
              marginBottom: 4,
            }}
          >
            Recommandé
          </span>
          <div
            style={{
              fontSize: 17,
              fontWeight: 800,
              letterSpacing: '-0.015em',
              lineHeight: 1.15,
              marginBottom: 3,
            }}
          >
            Prendre une photo
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
            Plaque ou n° de parc — l&rsquo;app reconnaît le camion
          </div>
        </span>
        <span style={{ flexShrink: 0, color: 'var(--teal)' }}>
          <Icon name="arrow-right" size={20} />
        </span>
      </button>

      <button
        type="button"
        onClick={() => navigate(`/client/${clientId}/new`)}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 16,
          padding: '18px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          cursor: 'pointer',
          textAlign: 'left',
          color: 'var(--text-primary)',
          fontFamily: 'inherit',
          width: '100%',
        }}
      >
        <span
          style={{
            width: 50,
            height: 50,
            borderRadius: 13,
            background: 'var(--bg-card-strong)',
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="edit" size={26} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 17,
              fontWeight: 800,
              letterSpacing: '-0.015em',
              lineHeight: 1.15,
              marginBottom: 3,
            }}
          >
            Entrée manuelle
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
            Saisir immat, n° de parc et prestations à la main
          </div>
        </span>
        <span style={{ flexShrink: 0, color: 'var(--text-faint)' }}>
          <Icon name="arrow-right" size={20} />
        </span>
      </button>

      <div
        style={{
          marginTop: 6,
          textAlign: 'center',
          fontSize: 11.5,
          color: 'var(--text-muted)',
          lineHeight: 1.4,
        }}
      >
        La photo évite <b style={{ color: 'var(--teal)', fontWeight: 700 }}>90 % des erreurs</b> de saisie.
      </div>
    </div>
  );
};

export default ClientNewChoice;
